import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Copy,
  ExternalLink,
  Radio,
  ShieldCheck,
} from "lucide-react";
import Modal from "../common/Modal";
import Button from "../common/Button";
import Notice from "../common/Notice";
import {
  CONNECT_POLL_MS,
  TRADINGVIEW_URL,
  TROUBLESHOOT_STEPS,
  connectPhase,
  lastSignalMs,
  strategyWebhook,
  venueLabel,
} from "./tradingViewConnect";

/**
 * TradingViewConnectWizard (SUPERBETA-8) — three steps, one copy button, a
 * green check.
 *
 * THE BRIEF, verbatim: "I need the connection to TradingView as simple as
 * possible. Apple simple. Anybody can do this." So the member's whole mental
 * model is: pick a strategy → copy one thing → paste it in TradingView → see a
 * green check. Every piece of engine machinery behind that — enrolment, the
 * credential in the URL, the signal record we poll — is invisible, and the words
 * for them do not appear on screen. "Webhook URL" is the single exception, and
 * only as the step-2 field label, because that is the exact label TradingView
 * puts on the box the member is pasting into. A different word there would make
 * the screen harder to follow, not easier.
 *
 * WHAT IS REAL HERE. The URL is the engine's own, from POST /enroll. The green
 * check is the engine's own record of a received signal, from
 * /status.recentSignals — this component never decides a connection succeeded,
 * it only reports that the engine says one did. See tradingViewConnect.js for
 * the ratified/not-ratified split.
 *
 * SIGNS NOTHING. There is no transaction anywhere in this flow: connecting a
 * signal source is a copy and a paste. The wallet signing that DOES exist on
 * this tab (the trading delegate) is untouched and unrelated.
 *
 * COMPLIANCE. No return, income or outcome language anywhere: "connected" means
 * signals are reaching the engine, and says only that. The final step carries
 * the risk line and a way through to the tab's full disclosure.
 *
 * POLLING. Step 3 doesn't open its own connection to the engine — it asks the
 * tab's single /status owner (useEngineStatus) for a forced read every
 * CONNECT_POLL_MS via `onPoll`, so the rest of the tab stays in step with what
 * the wizard is seeing and there is only ever one poller.
 */

const STEP_TITLES = ["Pick your strategy", "Copy your link", "Check it's working"];

/* ── step chrome ──────────────────────────────────────────────────────────── */

const StepHeader = React.forwardRef(({ step, title, hint }, ref) => (
  <div className="mb-5">
    <p className="text-caption uppercase tracking-[0.14em] text-arc-ink-3 mb-1.5">
      Step {step} of 3
    </p>
    {/* tabIndex -1 + focus on step change: a keyboard member who presses
        Continue lands on the new step's heading, not back at the top of the
        document with no idea the screen moved. */}
    <h3 ref={ref} tabIndex={-1} className="text-h2 text-arc-ink outline-none">
      {title}
    </h3>
    {hint && <p className="text-body text-arc-ink-2 mt-1.5">{hint}</p>}
  </div>
));
StepHeader.displayName = "StepHeader";

const FactRow = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 py-2.5 border-b border-arc-line last:border-b-0">
    <span className="text-caption uppercase tracking-[0.1em] text-arc-ink-3">{label}</span>
    <span className="text-body text-arc-ink text-right">{value}</span>
  </div>
);

/* ── step 1 ───────────────────────────────────────────────────────────────── */

const StepPick = ({ pair, venue, allocationPct }) => (
  <div
    className="bg-arc-paper-2 ring-1 ring-arc-line rounded-arc-ctl px-4 py-1"
    data-testid="sa-tv-confirm-card"
  >
    <FactRow label="Strategy" value={<span className="font-semibold">{pair}</span>} />
    <FactRow label="Trades on" value={venueLabel(venue)} />
    <FactRow
      label="Your allocation"
      value={`${Math.round(Number(allocationPct) || 0)}%`}
    />
  </div>
);

/* ── step 2 ───────────────────────────────────────────────────────────────── */

const CopyBox = ({ label, value, copied, onCopy, testid, big = false }) => (
  <div>
    {/* Not a <label>: the value below is static text, not a form control, and a
        label pointing at a div names nothing to a screen reader. aria-labelledby
        on a focusable box is what actually announces "Webhook URL" and then the
        value — and the box is tabbable so a keyboard member can reach it and
        select it by hand if they'd rather not use the button. */}
    <p
      id={`${testid}-label`}
      className="text-caption uppercase tracking-[0.1em] text-arc-ink-3 mb-1.5"
    >
      {label}
    </p>
    {/* Shown in full and wrapped, never truncated or ellipsised: a member who
        can't trust that the box holds the whole thing will try to select it by
        hand, and half a link is worse than no link. */}
    <div
      data-testid={testid}
      tabIndex={0}
      role="group"
      aria-labelledby={`${testid}-label`}
      className={[
        "text-xs text-arc-ink-2 bg-arc-paper-2 ring-1 ring-arc-line rounded-arc-ctl p-3",
        "break-all font-mono select-all focus:outline-none focus:ring-arc-accent",
      ].join(" ")}
    >
      {value}
    </div>
    <Button
      type="button"
      onClick={onCopy}
      className={big ? "w-full mt-3 py-3 text-[15px]" : "mt-2"}
      variant={big ? "primary" : "secondary"}
      data-testid={`${testid}-copy`}
    >
      {copied ? <Check size={big ? 18 : 14} aria-hidden="true" /> : <Copy size={big ? 18 : 14} aria-hidden="true" />}
      {copied ? "Copied" : `Copy ${big ? "link" : "message"}`}
    </Button>
  </div>
);

const StepCopy = ({ webhook, copiedKey, onCopy, copyFailed, onEnroll, enrollBusy }) => {
  if (!webhook.ready) {
    /* ARC-52: this used to promise the link would "appear here by itself",
       which held only while enrollment was an unsigned probe the tab could fire
       on mount. It needs one free signature now, so the member has to ask for
       it — and the wizard offers it here rather than sending them away. */
    return (
      <Notice tone="warn" data-testid="sa-tv-not-ready">
        <p>
          Your personal link isn&apos;t ready yet. It needs one signature from
          your wallet to prove this account is yours. That signature is free, it
          isn&apos;t a transaction, and it gives no one permission to move your
          funds.
        </p>
        {onEnroll ? (
          <button
            type="button"
            onClick={onEnroll}
            disabled={enrollBusy}
            data-testid="sa-tv-enroll"
            className="mt-2 inline-flex items-center gap-1.5 text-caption text-arc-ink rounded-arc-sm px-3 py-1.5 ring-1 ring-arc-line hover:bg-arc-paper-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {enrollBusy ? "Waiting for your signature…" : "Sign to generate my link"}
          </button>
        ) : null}
      </Notice>
    );
  }

  return (
    <div className="space-y-5">
      <CopyBox
        label="Webhook URL"
        value={webhook.url}
        copied={copiedKey === "url"}
        onCopy={() => onCopy(webhook.url, "url")}
        testid="sa-tv-url"
        big
      />

      {copyFailed && (
        <p className="text-caption text-arc-warn" role="status" data-testid="sa-tv-copy-failed">
          This browser wouldn&apos;t let us copy for you. Select the text above
          and copy it yourself — it works exactly the same.
        </p>
      )}

      {/* The three lines, in the order the member does them. */}
      <ol className="space-y-2.5" data-testid="sa-tv-guide">
        {[
          "In TradingView, open your strategy's alert.",
          "Paste this into the alert's “Webhook URL” box.",
          "Save the alert.",
        ].map((line, i) => (
          <li key={line} className="flex gap-3 items-start">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-arc-accent-soft text-arc-accent-ink text-caption font-semibold inline-flex items-center justify-center">
              {i + 1}
            </span>
            <span className="text-body text-arc-ink-2">{line}</span>
          </li>
        ))}
      </ol>

      {/* Not ratified: one URL can't name the strategy, so TradingView also
          needs the message that does. Secondary weight, because it is one more
          paste and not the thing this step is about — but present, because a
          step 2 that quietly dropped it would look finished and not work.
          The day the engine ships per-strategy URLs this whole block goes. */}
      {!webhook.carriesStrategy && webhook.alertMessage && (
        <details className="rounded-arc-ctl ring-1 ring-arc-line bg-arc-paper-2 p-3">
          <summary className="text-body text-arc-ink cursor-pointer select-none">
            One more paste: the alert message
          </summary>
          <p className="text-caption text-arc-ink-3 mt-2 mb-3">
            In the same alert box, paste this as the message. It&apos;s what tells
            us the alert is for {webhook.pairLabel}.
          </p>
          <CopyBox
            label="Alert message"
            value={webhook.alertMessage}
            copied={copiedKey === "msg"}
            onCopy={() => onCopy(webhook.alertMessage, "msg")}
            testid="sa-tv-msg"
          />
        </details>
      )}

      <details className="rounded-arc-ctl ring-1 ring-arc-line p-3">
        <summary className="text-body text-arc-ink cursor-pointer select-none">
          What&apos;s this?
        </summary>
        <p className="text-body text-arc-ink-2 mt-2">
          This link is yours alone. When your TradingView alert fires, TradingView
          sends it straight to ARC, and ARC acts on it for the strategy you chose.
          ARC never sees your TradingView account and never asks for your
          TradingView password. Keep the link private — anyone who has it can send
          alerts to your account.
        </p>
      </details>

      <a
        href={TRADINGVIEW_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-body text-arc-accent-ink underline underline-offset-4"
        data-testid="sa-tv-open-tradingview"
      >
        <ExternalLink size={15} aria-hidden="true" />
        Open TradingView
      </a>
    </div>
  );
};

/* ── step 3 ───────────────────────────────────────────────────────────────── */

const StepCheck = ({ phase, pair, onOpenDisclosure, manageDisabledNote }) => (
  <div data-testid={`sa-tv-phase-${phase}`}>
    {phase === "connected" ? (
      <div className="text-center py-2" data-testid="sa-tv-connected">
        <CheckCircle2
          size={56}
          className="mx-auto text-arc-pos mb-3"
          aria-hidden="true"
          data-testid="sa-tv-green-check"
        />
        <p className="text-h2 text-arc-ink mb-1">Connected — signals flowing</p>
        <p className="text-body text-arc-ink-2">
          ARC is receiving your {pair} alerts from TradingView.
        </p>
      </div>
    ) : (
      <div className="text-center py-2">
        <Radio
          size={40}
          className="mx-auto text-arc-accent-ink mb-3 animate-pulse"
          aria-hidden="true"
        />
        <p className="text-h2 text-arc-ink mb-1" role="status">
          Waiting for your first signal…
        </p>
        <p className="text-body text-arc-ink-2">
          In TradingView, press <span className="font-semibold">Test</span> on your
          alert to send one now. This screen updates by itself.
        </p>
      </div>
    )}

    {phase === "quiet" && (
      <div className="mt-5" data-testid="sa-tv-troubleshoot">
        <p className="text-body text-arc-ink mb-2.5">
          Nothing has arrived yet. Three things worth checking:
        </p>
        <ul className="space-y-2 list-disc pl-5">
          {TROUBLESHOOT_STEPS.map((s) => (
            <li key={s} className="text-body text-arc-ink-2">
              {s}
            </li>
          ))}
        </ul>
        <p className="text-caption text-arc-ink-3 mt-3">
          We&apos;re still listening — if a signal lands while this is open, this
          screen turns green.
        </p>
      </div>
    )}

    {manageDisabledNote}

    {/* Compliance: the risk line and the way through to the full disclosure both
        live on the step the member finishes on. */}
    <Notice tone="warn" className="mt-5" data-testid="sa-tv-risk">
      <p>
        Automated trading of leveraged instruments carries substantial risk,
        including total loss. This is educational software,{" "}
        <span className="font-semibold">not financial advice</span>, and no outcome
        is promised. You keep custody of your funds throughout — ARC can open and
        close trades, never withdraw, and you can revoke that anytime.{" "}
        <button
          type="button"
          onClick={onOpenDisclosure}
          className="underline font-semibold text-arc-warn"
          data-testid="sa-tv-disclosure-link"
        >
          Read the full risk disclosure
        </button>
      </p>
    </Notice>
  </div>
);

/* ── the wizard ───────────────────────────────────────────────────────────── */

const TradingViewConnectWizard = ({
  open,
  pair,
  venue = "gtrade",
  allocationPct = 0,
  enrollment,
  /* ARC-52: enrollment needs the member's signature, so step 2 can offer it
     rather than dead-ending when the link isn't ready. */
  onEnroll,
  enrollBusy = false,
  status,
  /* () => Promise<status> — a FORCED /status read owned by the tab. Step 3 calls
     it on a timer; without it the wizard simply waits on the tab's own poll. */
  onPoll,
  onClose,
  onOpenDisclosure,
  /* "connect" walks all three steps. "manage" opens straight on step 3 for a row
     that is already connected. */
  mode = "connect",
}) => {
  const [step, setStep] = useState(mode === "manage" ? 3 : 1);
  const [copiedKey, setCopiedKey] = useState(null);
  const [copyFailed, setCopyFailed] = useState(false);
  const [baselineMs, setBaselineMs] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);

  const headingRef = useRef(null);
  const statusRef = useRef(status);
  statusRef.current = status;
  const startedRef = useRef(null);

  /* Opening is what resets the wizard — including the baseline that decides
     what "connected" means for this visit. In manage mode there is no baseline:
     the member is looking at a connection that already exists, and any signal on
     record is the proof of it. In connect mode the baseline is the newest signal
     at open, so a signal from last month cannot paint a green check over a paste
     that never landed. */
  useEffect(() => {
    if (!open) return;
    setStep(mode === "manage" ? 3 : 1);
    setCopiedKey(null);
    setCopyFailed(false);
    setElapsedMs(0);
    startedRef.current = Date.now();
    setBaselineMs(mode === "manage" ? null : lastSignalMs(statusRef.current, pair));
  }, [open, pair, mode]);

  useEffect(() => {
    if (!open) return;
    headingRef.current?.focus();
  }, [open, step]);

  const lastMs = lastSignalMs(status, pair);
  const phase = connectPhase({ lastMs, baselineMs, elapsedMs });

  /* Ask the tab for a fresh read while we're on step 3 and still waiting. The
     poll keeps running past the troubleshoot timeout on purpose — a member who
     fixes their alert at minute eleven should still get the green check. */
  useEffect(() => {
    if (!open || step !== 3 || phase === "connected") return undefined;
    const id = setInterval(() => {
      setElapsedMs(Date.now() - (startedRef.current ?? Date.now()));
      onPoll?.();
    }, CONNECT_POLL_MS);
    return () => clearInterval(id);
  }, [open, step, phase, onPoll]);

  /* Step 3's clock starts when the member gets there, not when they opened the
     wizard: the ten minutes are a wait for a signal, not a reading allowance. */
  useEffect(() => {
    if (step !== 3) return;
    startedRef.current = Date.now();
    setElapsedMs(0);
  }, [step]);

  const webhook = useMemo(() => strategyWebhook(enrollment, pair), [enrollment, pair]);

  const copy = useCallback(async (text, key) => {
    setCopyFailed(false);
    try {
      if (!navigator?.clipboard?.writeText) throw new Error("no clipboard");
      await navigator.clipboard.writeText(String(text));
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1600);
    } catch {
      /* Honest, and recoverable: the text is on screen in full and selectable. */
      setCopyFailed(true);
    }
  }, []);

  if (!open) return null;

  const manageNote =
    mode === "manage" ? (
      <div className="mt-5 rounded-arc-ctl ring-1 ring-arc-line p-3" data-testid="sa-tv-manage">
        <p className="text-body text-arc-ink mb-2">Manage this connection</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" disabled title="Coming in beta">
            New link — coming in beta
          </Button>
          <Button variant="secondary" disabled title="Coming in beta">
            Turn off link — coming in beta
          </Button>
        </div>
        <p className="text-caption text-arc-ink-3 mt-2.5">
          To stop trading right now, use Revoke on your wallet panel — that works
          today and stops it on-chain, whatever any alert says.
        </p>
      </div>
    ) : null;

  const canContinue = step === 1 || (step === 2 && webhook.ready);

  const footer = (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      {step > 1 && mode === "connect" ? (
        <Button variant="secondary" onClick={() => setStep((s) => s - 1)}>
          <ChevronLeft size={15} aria-hidden="true" />
          Back
        </Button>
      ) : (
        <span />
      )}

      {step < 3 ? (
        <Button
          onClick={() => setStep((s) => s + 1)}
          disabled={!canContinue}
          data-testid="sa-tv-continue"
        >
          {step === 2 ? "I've pasted it" : "Continue"}
          <ChevronRight size={15} aria-hidden="true" />
        </Button>
      ) : (
        <Button onClick={onClose} data-testid="sa-tv-done">
          Done
        </Button>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === "manage" ? `${pair} · TradingView` : "Connect TradingView"}
      footer={footer}
    >
      <div className="text-left" data-testid="sa-tv-wizard" data-step={step}>
        {step === 1 && (
          <>
            <StepHeader
              ref={headingRef}
              step={1}
              title={STEP_TITLES[0]}
              hint="This is the strategy your TradingView alerts will drive."
            />
            <StepPick pair={pair} venue={venue} allocationPct={allocationPct} />
            <p className="text-caption text-arc-ink-3 mt-4 inline-flex items-start gap-2">
              <ShieldCheck size={14} className="flex-shrink-0 mt-0.5 text-arc-pos" aria-hidden="true" />
              Nothing to sign, and no money moves. You&apos;re connecting a signal
              source, that&apos;s all.
            </p>
          </>
        )}

        {step === 2 && (
          <>
            <StepHeader
              ref={headingRef}
              step={2}
              title={STEP_TITLES[1]}
              hint="Copy this, paste it into your TradingView alert, save."
            />
            <StepCopy
              webhook={webhook}
              copiedKey={copiedKey}
              onCopy={copy}
              copyFailed={copyFailed}
              onEnroll={onEnroll}
              enrollBusy={enrollBusy}
            />
          </>
        )}

        {step === 3 && (
          <>
            <StepHeader ref={headingRef} step={3} title={STEP_TITLES[2]} />
            <StepCheck
              phase={phase}
              pair={pair}
              onOpenDisclosure={onOpenDisclosure}
              manageDisabledNote={manageNote}
            />
          </>
        )}
      </div>
    </Modal>
  );
};

export default TradingViewConnectWizard;
